import { default as _log } from '@dw/loglevel';
import EventEmitter from "@dreamworld/event-emitter";
import { uuid } from "@dreamworld/uuid-base62";
import { AuthStatus } from "./auth-status.js";
import compact from 'lodash-es/compact.js';

export default class RemoteDatabase extends EventEmitter {
  constructor(dbName, couchdb) {
    super();
    this.couchdb = couchdb;
    this.dbName = dbName;
    //set logger
    this.log = _log.getLogger(`dreamdb.remoteDatabase.${this.couchdb.name}.${this.dbName}`);

    // logger prefix
    this.logPrefix = `dreamDB.couchDB.${this.couchdb.name}.db.${this.dbName}.RemoteDB`;

    this._requestedDocs = undefined;
    this._requestedDocsPromise = undefined;
  }

  /**
   * Note: Revision id of the input docs should represent the revision after the local write, as 
   * this is written on the remote db using `new_edits=false`.
   * 
   * @param {*} docs One or multiple documents to be written to the remote database.
   * @returns Promise which is resolved with the bulk-docs response. See 
   *   https://docs.couchdb.org/en/stable/api/database/bulk-api.html#db-bulk-docs
   */
  async save(docs, requestedAt) {
    let documents = compact(docs);

    if(!documents || !documents.length) {
      this.log.error(`${this.logPrefix}.save(): failed to save on remote. docs have invalid value. dbName=${this.dbName}, docs=`, docs, `requestedAt=`, requestedAt);
      reject();
      return;
    }

    docs = documents;

    if (this._requestedDocs) {
      this._requestedDocs = [...this._requestedDocs, ...docs];
      return this._requestedDocsPromise;
    }

    let resolve;
    let reject;

    this._requestedDocs = [...docs];
    this._requestedDocsPromise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });

    setTimeout(() => {
      //write docs through localStorage

      let requestId = uuid();
      let value = {
        dbName: this.dbName,
        docs: this._requestedDocs,
        requestedAt
      };

      let localStorageKey = `dreamdb.${this.couchdb.name}.writeDocs.${requestId}`;

      window.localStorage.setItem(localStorageKey, JSON.stringify(value));
      this.log.debug(`${this.logPrefix}.save(): Requested to save docs on remote. requestId=${requestId}, docs=`, this._requestedDocs);

      if (this.couchdb.master) {
        this.couchdb.handleNewWritesRequest({ key: localStorageKey, newValue: JSON.stringify(value) });
      }

      let localChangesHandler = (e) => {
        if ((e.key || e.detail.key) === localStorageKey) {
          let newValue = e.newValue || e.detail.newValue;
          let value = JSON.parse(newValue);
          let response = value.response;
          if (response.error) {
            window.localStorage.removeItem(localStorageKey);
            window.removeEventListener('storage', localChangesHandler);
            this.log.warn(`${this.logPrefix}.save(): remote save failed with requestId=${requestId} and response=`, response);
            reject(response);
            return;
          }

          //remove from localStorage
          window.localStorage.removeItem(localStorageKey);
          window.removeEventListener('storage', localChangesHandler);
          this.log.debug(`${this.logPrefix}.save(): remote save completed with requestId=${requestId} and response=`, response);
          resolve(response);
        }
      };

      window.addEventListener('storage', localChangesHandler);
      this._requestedDocs = undefined;
      this._requestedDocsPromise = undefined;
    }, 45);

    return this._requestedDocsPromise;
  }

  /**
   * 
   * @param {*} param0 
   * @returns Promise which is resolved when server acknowledges this query is performed.
   */
  async queryByIds({ queryId, docIds, once }) {
    let request = {
      id: queryId,
      type: 'IDS',
      db: this.dbName,
      docIds,
      once
    };

    return this._emit('QUERY', request);
  }

  async updateQueryByIds({ queryId, docIds }) {
    return this.queryByIds({ queryId, docIds });
  }

  async queryFind(query) {
    let { id, selector, useIndex, limit, skip, sort, once } = query;

    let request = {
      id,
      type: 'FIND',
      db: this.dbName,
      selector,
      index: useIndex,
      limit,
      skip,
      sort,
      once
    };

    return this._emit('QUERY', request);
  }

  async updateQueryFind(query) {
    return this.queryFind(query);
  }

  async queryView(query) {
    let { id, view, startkey, endkey, limit, skip, descending, key, keys, inclusive_end, once } = query;

    let request = {
      id,
      type: 'VIEW',
      db: this.dbName,
      view,
      startkey,
      limit,
      skip,
      endkey,
      descending,
      key,
      keys,
      inclusive_end,
      once
    };

    return this._emit('QUERY', request);
  }

  async updateQueryView(query) {
    return this.queryView(query);
  }

  async cancelQuery(id) {
    return this._emit('CLOSE_QUERY', { id });
  }

  /**
   * It sends message on couchdb.socket, and if it fails due to authentication error; it’s retried once 
   * after authentication is completed.
   * @param {String} type 
   * @param {String|Object} message
   * @returns {Promise}
   */
  async _emit(type, message, id) {
    id = id || uuid();
    const logPrefix = `_emit: id=${id}; `;
    this.log.trace(logPrefix, type, message);

    const authStatus = this.couchdb.authStatus;
    //when authStatus is already in final stage, request doesn't need to be ever retried.
    if (authStatus === AuthStatus.DONE || authStatus === AuthStatus.INVALID_CREDENTIAL) {
      return new Promise((resolve) => {
        this.couchdb.socket.emit(type, message, (response) => {
          this.log.trace(`${logPrefix} response available`);
          resolve(response);
        });
      });
    }

    if (authStatus === AuthStatus.IN_PROGRESS) {
      this.log.trace(`${logPrefix} As authStatus is IN_PROGRESS will wait till it becomes final`);
      await this._waitTillAuthStatusBecomesFinal();
      this.log.trace(`${logPrefix} going to retry`);
      return this._emit(type, message, id);
    }

    //Here, authStatus is PENDING. So, try once immediately. And if that fails with
    //`unauthorized` error and at that time authStatus has been changed; retry once
    //more.
    return new Promise((resolve) => {
      this.couchdb.socket.emit(type, message, async (response) => {
        if (!response.error || response.error !== `unauthorized`) {
          this.log.trace(`${logPrefix} response available`);
          resolve(response);
          return;
        }

        const newAuthStatus = this.couchdb.authStatus;
        this.log.trace(`${logPrefix} status changed from PENDING to ${newAuthStatus}.` +
          'So, will be retried again when authStatus becomes final.');
        if (newAuthStatus !== AuthStatus.PENDING) {
          await this._waitTillAuthStatusBecomesFinal();
          return this._emit(type, message);
        }

        resolve(response);
      });
    });
  }

  /**
   * It returns a promise which is resolved when couchdb's authStatus is changed to the final status
   * (either DONE or INVALID_CREDENTIAL). It’s never rejected.
   * @returns {Promise}
   */
  _waitTillAuthStatusBecomesFinal() {
    return new Promise((resolve) => {
      let authStatus = this.couchdb.authStatus;
      if (authStatus === AuthStatus.DONE || authStatus === AuthStatus.INVALID_CREDENTIAL) {
        resolve();
        return;
      }

      const listener = (status) => {
        if (status === AuthStatus.DONE || status === AuthStatus.INVALID_CREDENTIAL) {
          resolve();
          this.couchdb.off('auth-status-changed', listener);
        }
      }

      this.couchdb.on(`auth-status-changed`, listener);
    });
  }
}
